import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Table, Badge, Button, Alert } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const SSPList = () => {
  const navigate = useNavigate();
  const [ssps, setSSPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchSSPs();
  }, []);

  const fetchSSPs = async () => {
    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.get(`${baseURL}/ssp-studio/ssps`, {
        withCredentials: true
      });
      setSSPs(response.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching SSPs:', err);
      setError(err.response?.data?.message || err.message);
      setLoading(false);
    }
  };

  const deleteSSP = async (sspId, sspName) => {
    if (!window.confirm(`Are you sure you want to delete "${sspName}"? This will also delete all sections.`)) {
      return;
    }

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      await axios.delete(`${baseURL}/ssp-studio/ssps/${sspId}`, {
        withCredentials: true
      });
      setSuccess('SSP deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
      fetchSSPs();
    } catch (err) {
      console.error('Error deleting SSP:', err);
      setError(err.response?.data?.message || err.message);
    }
  };

  const updateSSPStatus = async (sspId, newStatus) => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      await axios.put(
        `${baseURL}/ssp-studio/ssps/${sspId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      setSuccess(`SSP status updated to ${newStatus}!`);
      setTimeout(() => setSuccess(null), 3000);
      fetchSSPs();
    } catch (err) {
      console.error('Error updating SSP status:', err);
      setError(err.response?.data?.message || err.message);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'secondary',
      'in-review': 'warning',
      approved: 'success',
      archived: 'dark'
    };
    return <Badge color={colors[status] || 'info'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Row className="mb-3">
        <Col lg={12}>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">System Security Plans</h4>
            <Button color="primary" onClick={() => navigate('/ssp-studio/create')}>
              <i className="mdi mdi-plus me-1"></i>
              Create New SSP
            </Button>
          </div>
        </Col>
      </Row>

      {success && (
        <Alert color="success" toggle={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              {ssps.length === 0 ? (
                <div className="text-center py-4">
                  <i className="mdi mdi-file-document-multiple font-size-24 text-muted mb-3"></i>
                  <h5 className="text-muted">No SSPs Found</h5>
                  <p className="text-muted">Create your first System Security Plan to get started</p>
                  <Button color="primary" onClick={() => navigate('/ssp-studio/create')}>
                    <i className="mdi mdi-plus me-1"></i>
                    Create SSP
                  </Button>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>System Name</th>
                      <th>Status</th>
                      <th>Template</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ssps.map((ssp) => (
                      <tr key={ssp._id}>
                        <td>
                          <span
                            className="text-primary fw-medium cursor-pointer"
                            onClick={() => navigate(`/ssp-studio/ssp/${ssp._id}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            {ssp.name}
                          </span>
                        </td>
                        <td>{ssp.metadata?.systemName || '-'}</td>
                        <td>{getStatusBadge(ssp.status)}</td>
                        <td>{ssp.template_id?.name || '-'}</td>
                        <td>{new Date(ssp.createdAt).toLocaleDateString()}</td>
                        <td>
                          <Button
                            size="sm"
                            color="info"
                            outline
                            className="me-1"
                            onClick={() => navigate(`/ssp-studio/ssp/${ssp._id}`)}
                          >
                            <i className="mdi mdi-eye"></i>
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            outline
                            onClick={() => deleteSSP(ssp._id, ssp.name)}
                          >
                            <i className="mdi mdi-delete"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default SSPList;
